import {Fragment, useContext, useState, useEffect, useRef} from "react";
import {useQuery} from "react-query";

import AuthContext from "../../../context/AuthContext";

import {fetcher, hasLoginError} from "../../../functions/requestFetcher";

import MessageRenderer from "../messageRenderer/messageRenderer";
import Spinner from "../Loading/Spinner";

import {getTextFromArr} from "../../../functions/getJsxFromArr";
import {deleteJunkCache} from "../../../react-query-client";

const parseLRC = (lrcString) => {
  if (!lrcString) return [];
  const lines = lrcString.split(/\r?\n/);
  const result = [];
  const timeRegex = /\[(\d{2}):(\d{2})\.(\d{2,3})\]/;
  
  for (const line of lines) {
    const match = timeRegex.exec(line);
    if (match) {
      const minutes = parseInt(match[1]);
      const seconds = parseInt(match[2]);
      const milliseconds = parseInt(match[3]);
      const time = minutes * 60 + seconds + milliseconds / (match[3].length === 3 ? 1000 : 100);
      const text = line.replace(timeRegex, "").trim();
      // Even empty text lines are important for timing, but we usually want text
      result.push({ time, text: text || "..." });
    }
  }
  return result.sort((a, b) => a.time - b.time);
};

function LyricsTab({lyrics, footer, isSynced}) {
  const [currentTime, setCurrentTime] = useState(0);
  const lyricsContainerRef = useRef(null);
  const activeLineRef = useRef(null);

  useEffect(() => {
    const handleTimeUpdate = (e) => {
      setCurrentTime(e.detail);
    };
    window.addEventListener("audio-time-update", handleTimeUpdate);
    return () => window.removeEventListener("audio-time-update", handleTimeUpdate);
  }, []);

  const syncedLines = isSynced ? parseLRC(lyrics) : null;

  useEffect(() => {
    if (activeLineRef.current) {
      activeLineRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentTime]);

  if (isSynced && syncedLines?.length > 0) {
    return (
      <div className="lyrics-container synced" ref={lyricsContainerRef}>
        {syncedLines.map((line, i) => {
          const isNextLine = syncedLines[i + 1] ? currentTime < syncedLines[i + 1].time : true;
          const isActive = currentTime >= line.time && isNextLine;
          
          return (
            <p
              key={i}
              ref={isActive ? activeLineRef : null}
              className={`lyrics-line ${isActive ? "active" : "inactive"}`}
              style={{
                fontSize: isActive ? "2.4rem" : "2rem",
                fontWeight: isActive ? "700" : "500",
                color: isActive ? "#ffffff" : "rgba(255, 255, 255, 0.3)",
                margin: "1.5rem 0",
                transition: "all 0.3s ease",
                cursor: "pointer",
                filter: isActive ? "none" : "blur(0.5px)",
              }}
              onClick={() => {
                // Optional: seek to time?
              }}
            >
              {line.text}
            </p>
          );
        })}
        {footer && <p className="lyrics-footer mg-t-4" style={{opacity: 0.5}}>{footer}</p>}
      </div>
    );
  }

  const formattedLyrics = lyrics && lyrics.replace(/\r?\n|\r/g, "<br>");
  const formattedFooter = footer && footer.replace(/\r?\n|\r/g, "<br>");
  return (
    <Fragment>
      {formattedLyrics && (
        <p
          className="lyrics text-small white "
          style={{ lineHeight: "1.8", fontSize: "1.8rem" }}
          dangerouslySetInnerHTML={{
            __html: `${formattedLyrics}`,
          }}></p>
      )}

      {formattedFooter && <p className="mg-t-2" style={{opacity: 0.5}}>{formattedFooter}</p>}
    </Fragment>
  );
}

function Lyrics({browseId, logout, artist, title}) {
  const {token} = useContext(AuthContext);

  const extractText = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (Array.isArray(val)) return val.map(v => v?.text || v?.name || v).join(" ");
    return val?.text || val?.name || String(val);
  };

  const cleanArtist = extractText(artist);
  const cleanTitle = extractText(title);

  const {data, error, isError, isSuccess} = useQuery(
    ["lyrics", browseId, cleanArtist, cleanTitle],
    () => {
      const artistParam = cleanArtist ? `&artist=${encodeURIComponent(cleanArtist)}` : "";
      const titleParam = cleanTitle ? `&title=${encodeURIComponent(cleanTitle)}` : "";
      return fetcher(
        {
          url: `${window.__SOUNDREX_API_URL__}/api/v1/lyrics/browse?id=${browseId}${artistParam}${titleParam}`,
          method: "GET",
          headers: {
            authorization: "Bearer " + token,
          },
        },
        false,
        false,
        false,
        null,
        null
      );
    },
    {
      staleTime: 300000,
      select: data => {
        deleteJunkCache("lyrics");
        return data;
      },
    }
  );

  let content = (
    <div className="flex-center mg-t-3">
      <Spinner />
    </div>
  );

  if (isError && error) {
    const hasLoginErr = hasLoginError(error);
    if (hasLoginErr) {
      logout();
    }
    content = <p className="centered capitalize text-medium">No lyrics found.</p>;
  }

  if (isSuccess) {
    if (data?.list || data?.message?.messageRenderer) {
      const isMessageRenderer = data?.message?.messageRenderer;

      if (!isMessageRenderer) {
        for (let item of data?.list) {
          if (item.hasLyrics) {
            const lyrics = item?.lyrics && getTextFromArr(item.lyrics);
            const footer = item?.footer && getTextFromArr(item.footer);
            content = <LyricsTab lyrics={lyrics} footer={footer} isSynced={item.isSynced} />;
            break;
          }
        }
      } else {
        content = <MessageRenderer msg={data?.message} />;
      }
    } else {
      content = <p className="centered capitalize text-medium">No lyrics found.</p>;
    }
  }

  return <Fragment>{content}</Fragment>;
}

export default Lyrics;

